import React, {
  useState,
  useEffect,
  useReducer,
  useMemo,
} from "react";
import GlobalContext from "./GlobalContext";
import dayjs from "dayjs";
import { createDummyEvents } from "../util";
import EventsService from "../services/events-service";
import UsersService from "../services/users-service";
import { AuthContext } from 'react-oauth2-code-pkce';

function savedEventsReducer(state, { type, payload }) {
  switch (type) {
    case "push":
      return [...state, payload];
    case "update":
      return state.map((evt) =>
        evt.id === payload.id ? payload : evt
      );
    case "delete":
      return state.filter((evt) => evt.id !== payload.id);
    case "set":
      return payload; // For API events
    case "refetch":
      return []; // Clear all events before refetching
    default:
      throw new Error();
  }
}
function initEvents() {
  var storageEvents = localStorage.getItem("savedEvents");
  
  const parsedEvents = storageEvents ? JSON.parse(storageEvents) : [];
  return parsedEvents;
}

export default function ContextWrapper(props) {
  const [monthIndex, setMonthIndex] = useState(dayjs().month());
  const [smallCalendarMonth, setSmallCalendarMonth] = useState(null);
  const [daySelected, setDaySelected] = useState(dayjs());
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [labels, setLabels] = useState([]);
  const [labelCache, setLabelCache] = useState({}); // Cache labels per calendar ID
  const [savedEvents, dispatchCalEvent] = useReducer(
    savedEventsReducer,
    [],
    initEvents
  );
  const [loadError, setLoadError] = useState(null);
  const [activeCalendarId, setActiveCalendarId] = useState(null);
  const [copiedEvent, setCopiedEvent] = useState(null);
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, type: '', event: null, day: null });
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoading, setUserLoading] = useState(false);
  const { token } = React.useContext(AuthContext);

  // Fetch user info when token is available
  const fetchUserInfo = React.useCallback(() => {
    if (token) {
      setUserLoading(true);
      return UsersService.getMe(token)
        .then(user => {
          setCurrentUser(user);
        })
        .catch((err) => {
          console.error('Failed to fetch user info:', err);
          setCurrentUser(null);
        })
        .finally(() => {
          setUserLoading(false);
        });
    }
    return Promise.resolve();
  }, [token]);

  useEffect(() => {
    fetchUserInfo();
  }, [token, fetchUserInfo]);

  // Fetch events from API when monthIndex changes or refetch is triggered
  const fetchEvents = React.useCallback(() => {
    // Use window.currentDate if available, else fallback to current year
    let year = dayjs().year();
    if (typeof window !== 'undefined' && window.currentDate && window.currentDate.year) {
      year = window.currentDate.year();
    }
    if (token && activeCalendarId) {
      setLoadError(null);
      return EventsService.fetchEvents(monthIndex, year, token, activeCalendarId)
        .then(events => {
          dispatchCalEvent({ type: 'set', payload: events });
        })
        .catch((err) => {
          dispatchCalEvent({ type: 'set', payload: [] }); // Clear events on error
          setLoadError('Fout bij laden van events van de server.');
        });
    }
    return Promise.resolve();
  }, [monthIndex, token, activeCalendarId]);

  useEffect(() => {
    fetchEvents();
  }, [monthIndex, token, activeCalendarId, fetchEvents]);

  const filteredEvents = useMemo(() => {
    if (!labels || labels.length === 0) {
      return savedEvents;
    }
    const hiddenIds = new Set(
      labels.filter((l) => l.visible === false).map((l) => l.id)
    );
    if (hiddenIds.size === 0) {
      return savedEvents;
    }
    return savedEvents.filter(
      (evt) => !evt.label || !hiddenIds.has(evt.label)
    );
  }, [savedEvents, labels]);

  useEffect(() => {
    localStorage.setItem("savedEvents", JSON.stringify(savedEvents));
  }, [savedEvents]);

  // Remove the useEffect that sets labels from savedEvents

  useEffect(() => {
    if (smallCalendarMonth !== null) {
      setMonthIndex(smallCalendarMonth);
    }
  }, [smallCalendarMonth]);

  useEffect(() => {
    if (!showEventModal) {
      setSelectedEvent(null);
    }
  }, [showEventModal]);

  // Global keyboard shortcuts for copy-paste
  useEffect(() => {
    function handleKeyDown(event) {
      // Only handle keyboard shortcuts if no modal is open and user is not readonly
      if (showEventModal || !token || isReadonly) return;

      if ((event.ctrlKey || event.metaKey) && event.key === 'c' && selectedEvent) {
        event.preventDefault();
        // Copy the selected event
        const eventToCopy = {
          ...selectedEvent,
          projection: false,
          recurrenceType: 'NONE',
          recurrenceInterval: null,
          recurrenceEndDate: null,
        };
        setCopiedEvent(eventToCopy);
        
        // Show feedback
        const originalTitle = document.title;
        document.title = '📋 Event gekopieerd!';
        setTimeout(() => {
          document.title = originalTitle;
        }, 1500);
      }

      if ((event.ctrlKey || event.metaKey) && event.key === 'v' && copiedEvent && daySelected) {
        event.preventDefault();
        // Paste to selected day
        const targetDayUtc = Date.UTC(
          daySelected.year(),
          daySelected.month(),
          daySelected.date(),
          0, 0, 0, 0
        );
        
        const newEvent = {
          ...copiedEvent,
          id: undefined,
          day: targetDayUtc,
        };

        // Check for conflicts
        const dayEvents = filteredEvents.filter(evt => evt.day === targetDayUtc);
        const hasConflict = dayEvents.some(evt => {
          if (!evt.startTime || !evt.endTime || !newEvent.startTime || !newEvent.endTime) return false;
          const [aStartH, aStartM] = evt.startTime.split(":").map(Number);
          const [aEndH, aEndM] = evt.endTime.split(":").map(Number);
          const [bStartH, bStartM] = newEvent.startTime.split(":").map(Number);
          const [bEndH, bEndM] = newEvent.endTime.split(":").map(Number);
          const aStart = aStartH * 60 + aStartM;
          const aEnd = aEndH * 60 + aEndM;
          const bStart = bStartH * 60 + bStartM;
          const bEnd = bEndH * 60 + bEndM;
          return aStart < bEnd && bStart < aEnd;
        });

        if (hasConflict) {
          alert('Kan event niet plakken: tijd overlapt met bestaande afspraak!');
          return;
        }

        // Save the event
        EventsService.saveEvent(newEvent, token)
          .then(() => {
            const originalTitle = document.title;
            document.title = '✅ Event geplakt!';
            setTimeout(() => {
              document.title = originalTitle;
            }, 1500);
            fetchEvents();
          })
          .catch(err => {
            alert('Fout bij plakken van event: ' + (err.message || err));
          });
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showEventModal, token, selectedEvent, copiedEvent, daySelected, filteredEvents, setCopiedEvent, fetchEvents]);

  function updateLabel(label) {
    setLabels(
      labels.map((lbl) => (lbl.label === label.label ? label : lbl))
    );
  }

  // Label cache management functions
  const cacheLabelsForCalendar = (calendarId, labelsData) => {
    setLabelCache(prev => ({
      ...prev,
      [calendarId]: labelsData
    }));
  };

  const getCachedLabelsForCalendar = (calendarId) => {
    return labelCache[calendarId] || null;
  };

  const clearLabelCache = () => {
    setLabelCache({});
  };

  // Calculate readonly status based on user data from /me endpoint
  // Admin users override readonly restrictions per the requirements
  const isReadonly = currentUser ? 
    (currentUser.isReadonly && !currentUser.isAdmin) : false;

  return (
    <GlobalContext.Provider
      value={{
        monthIndex,
        setMonthIndex,
        smallCalendarMonth,
        setSmallCalendarMonth,
        daySelected,
        setDaySelected,
        showEventModal,
        setShowEventModal,
        dispatchCalEvent,
        selectedEvent,
        setSelectedEvent,
        savedEvents,
        setLabels,
        labels,
        updateLabel,
        cacheLabelsForCalendar,
        getCachedLabelsForCalendar,
        clearLabelCache,
        filteredEvents,
        loadError, // pass error to context
        activeCalendarId,
        setActiveCalendarId,
        fetchEvents, // <-- provide fetchEvents in context
        copiedEvent,
        setCopiedEvent,
        contextMenu,
        setContextMenu,
        currentUser,
        userLoading,
        fetchUserInfo,
        isReadonly,
      }}
    >
      {props.children}
    </GlobalContext.Provider>
  );
}
